import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { injectIntl, FormattedMessage } from '../../util/reactIntl';
import css from './SubscriptionPage.css';
import Button from './Button';

const EditInvoiceForm = ({ initialData, onSave, onCancel, isSaving, intl }) => {
  const [form, setForm] = useState({
    customerType: initialData?.customerType || 'individual',
    name: initialData?.name || '',
    companyName: initialData?.companyName || '',
    taxId: initialData?.taxId || '',
    address: initialData?.address || '',
    zip: initialData?.zip || '',
    city: initialData?.city || '',
    country: initialData?.country || 'Polska',
    countryCode: initialData?.countryCode || 'PL',
  });

  const [errors, setErrors] = useState({});

  const isBusiness = form.customerType === 'company';

  // Automatyczne ustawienie i zablokowanie kraju na "Polska" dla firm
  useEffect(() => {
    if (isBusiness) {
      setForm(prev => ({
        ...prev,
        country: 'Polska',
        countryCode: 'PL',
      }));
      setErrors(prev => ({ ...prev, country: null }));
    }
  }, [isBusiness]);

  // Funkcja walidująca NIP (format PL + 10 cyfr)
  const validateNIP = (value) => {
    if (!value) {
      return intl.formatMessage({ id: 'EditInvoiceForm.error.taxIdRequired', defaultValue: 'Numer NIP jest wymagany dla firm' });
    }

    const cleanNIP = value.replace(/[\s-]/g, '').toUpperCase();

    const nipRegex = /^PL\d{10}$/;
    if (!nipRegex.test(cleanNIP)) {
      return intl.formatMessage({
        id: 'EditInvoiceForm.error.taxIdInvalidFormat',
        defaultValue: 'NIP musi zaczynać się od PL i zawierać 10 cyfr (np. PL1234567890)',
      });
    }

    const digits = cleanNIP.replace('PL', '').split('').map(Number);
    const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
    const sum = weights.reduce((acc, weight, idx) => acc + weight * digits[idx], 0);
    const checksum = sum % 11;

    if (checksum === 10 || checksum !== digits[9]) {
      return intl.formatMessage({
        id: 'EditInvoiceForm.error.taxIdChecksumInvalid',
        defaultValue: 'Wprowadzony numer NIP jest nieprawidłowy',
      });
    }

    return null;
  };

  // Ogólna walidacja pola (niepuste, min. 3 znaki)
  const validateField = (name, value) => {
    const trimmed = (value || '').trim();
    if (!trimmed) {
      return intl.formatMessage({ id: 'EditInvoiceForm.error.fieldRequired', defaultValue: 'To pole jest wymagane' });
    }
    if (trimmed.length < 3) {
      return intl.formatMessage({ id: 'EditInvoiceForm.error.fieldMinLength', defaultValue: 'Wymagane co najmniej 3 znaki' });
    }
    return null;
  };

  // Walidacja całego formularza
  const validateAll = () => {
    const newErrors = {};

    if (isBusiness) {
      const companyNameErr = validateField('companyName', form.companyName);
      if (companyNameErr) newErrors.companyName = companyNameErr;

      const taxIdErr = validateNIP(form.taxId);
      if (taxIdErr) newErrors.taxId = taxIdErr;
    } else {
      const nameErr = validateField('name', form.name);
      if (nameErr) newErrors.name = nameErr;
    }

    const addressErr = validateField('address', form.address);
    if (addressErr) newErrors.address = addressErr;

    const cityErr = validateField('city', form.city);
    if (cityErr) newErrors.city = cityErr;

    const zipErr = validateField('zip', form.zip);
    if (zipErr) newErrors.zip = zipErr;

    if (!isBusiness) {
      const countryErr = validateField('country', form.country);
      if (countryErr) newErrors.country = countryErr;
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }

    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const formErrors = validateAll();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit} className={css.invoiceForm} noValidate>
      <div className={css.formScrollArea}>
        <p className={css.modalDescription}>
          <FormattedMessage
            id="EditInvoiceForm.modalDescription"
            defaultValue="Zmiana danych NIE WPŁYWA na zmianę już wystawionych faktur. Nowe faktury będą wystawione na dane obowiązujące w dniu wystawienia faktury."
          />
        </p>

        <div className={css.inputGroup}>
          <label className={css.inputLabel}>
            <FormattedMessage id="EditInvoiceForm.customerType" defaultValue="Typ klienta" />
          </label>
          <select
            name="customerType"
            value={form.customerType}
            onChange={handleChange}
            className={css.selectInput}
          >
            <option value="individual">
              {intl.formatMessage({ id: 'EditInvoiceForm.individual', defaultValue: 'Osoba prywatna' })}
            </option>
            <option value="company">
              {intl.formatMessage({ id: 'EditInvoiceForm.company', defaultValue: 'Firma' })}
            </option>
          </select>
        </div>

        <div className={css.formGrid}>
          {isBusiness && (
            <>
              <div className={css.inputGroup}>
                <label className={css.inputLabel}>
                  <FormattedMessage id="EditInvoiceForm.companyName" defaultValue="Nazwa firmy" />
                </label>
                <input
                  name="companyName"
                  value={form.companyName}
                  onChange={handleChange}
                  className={classNames(css.textInput, { [css.inputError]: !!errors.companyName })}
                />
                {errors.companyName && <span className={css.fieldErrorText}>{errors.companyName}</span>}
              </div>

              <div className={css.inputGroup}>
                <label className={css.inputLabel}>
                  <FormattedMessage id="EditInvoiceForm.taxId" defaultValue="NIP" />
                </label>
                <input
                  name="taxId"
                  value={form.taxId}
                  onChange={handleChange}
                  placeholder="PL1234567890"
                  className={classNames(css.textInput, { [css.inputError]: !!errors.taxId })}
                />
                {errors.taxId && <span className={css.fieldErrorText}>{errors.taxId}</span>}
              </div>
            </>
          )}

          {!isBusiness && (
            <div className={classNames(css.inputGroup, css.fullWidth)}>
              <label className={css.inputLabel}>
                <FormattedMessage id="EditInvoiceForm.name" defaultValue="Imię i nazwisko" />
              </label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                className={classNames(css.textInput, { [css.inputError]: !!errors.name })}
              />
              {errors.name && <span className={css.fieldErrorText}>{errors.name}</span>}
            </div>
          )}

          <div className={css.rowInputs}>
            <div className={css.inputGroup}>
              <label className={css.inputLabel}>
                <FormattedMessage id="EditInvoiceForm.country" defaultValue="Kraj" />
              </label>
              <input
                name="country"
                value={form.country}
                onChange={handleChange}
                className={classNames(css.textInput, { [css.inputError]: !!errors.country })}
                disabled={isBusiness}
              />
              {errors.country && <span className={css.fieldErrorText}>{errors.country}</span>}
            </div>

            <div className={css.inputGroup}>
              <label className={css.inputLabel}>
                <FormattedMessage id="EditInvoiceForm.city" defaultValue="Miasto" />
              </label>
              <input
                name="city"
                value={form.city}
                onChange={handleChange}
                className={classNames(css.textInput, { [css.inputError]: !!errors.city })}
              />
              {errors.city && <span className={css.fieldErrorText}>{errors.city}</span>}
            </div>
          </div>

          <div className={css.rowInputs}>
            <div className={css.inputGroup}>
              <label className={css.inputLabel}>
                <FormattedMessage id="EditInvoiceForm.address" defaultValue="Adres" />
              </label>
              <input
                name="address"
                value={form.address}
                onChange={handleChange}
                className={classNames(css.textInput, { [css.inputError]: !!errors.address })}
              />
              {errors.address && <span className={css.fieldErrorText}>{errors.address}</span>}
            </div>

            <div className={css.inputGroup}>
              <label className={css.inputLabel}>
                <FormattedMessage id="EditInvoiceForm.zip" defaultValue="Kod pocztowy" />
              </label>
              <input
                name="zip"
                value={form.zip}
                onChange={handleChange}
                className={classNames(css.textInput, { [css.inputError]: !!errors.zip })}
              />
              {errors.zip && <span className={css.fieldErrorText}>{errors.zip}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className={css.modalFooter}>
        <Button type="button" variant="secondary" className={css.cancelBtn} onClick={onCancel} isLoading={isSaving}>
          <FormattedMessage id="EditInvoiceForm.btnCancel" defaultValue="Powrót" />
        </Button>
        <Button type="submit" className={css.saveBtn} isLoading={isSaving}>
          {isSaving ? (
            <FormattedMessage id="EditInvoiceForm.saving" defaultValue="Zapisywanie..." />
          ) : (
            <FormattedMessage id="EditInvoiceForm.btnSave" defaultValue="Zapisz" />
          )}
        </Button>
      </div>
    </form>
  );
};

export default injectIntl(EditInvoiceForm);